import React, { useEffect, useState } from "react";
import { defaultSiteConfig, type SiteConfig, type NotificationItem } from "../siteConfig";
import "./Navbar.css";

const navLinks = [
  { label: "Início", href: "#/home", key: "home" },
  { label: "Cursos", href: "#/curso", key: "curso" },
  { label: "Ferramentas", href: "#/catalogo", key: "catalogo" },
  { label: "Mercado", href: "#/utilidades", key: "utilidades" },
];

const PAGE_TITLES: Record<string, string> = {
  home: "Início",
  curso: "Cursos",
  catalogo: "Ferramentas",
  utilidades: "Mercado",
};

interface NavbarProps {
  config?: SiteConfig; // vem do App (getSiteConfig → admin/backend); default = exemplos
  userName?: string;
  onPrimaryAction: () => void;
  primaryLabel: string;
  currentPage: string;
  isLoggedIn: boolean;
  onUnlock?: () => void;
  onOpenCertificates?: () => void;
  progressPercent?: number;
}

function NavIcon({ pageKey }: { pageKey: string }) {
  switch (pageKey) {
    case "home":
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M4.75 10.5 12 4.85l7.25 5.65" /><path d="M7.5 10v8.75h9V10" /><path d="M10 18.75v-4.25h4v4.25" />
        </svg>
      );
    case "catalogo":
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <rect x="4" y="4" width="7" height="7" rx="1.6" /><rect x="13" y="4" width="7" height="7" rx="1.6" /><rect x="4" y="13" width="7" height="7" rx="1.6" /><rect x="13" y="13" width="7" height="7" rx="1.6" />
        </svg>
      );
    case "curso":
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M6.5 5.25h9.75A1.75 1.75 0 0 1 18 7v11.25H8.25A2.75 2.75 0 0 0 5.5 21V6.25a1 1 0 0 1 1-1Z" /><path d="M8 9h6.5M8 12h5" />
        </svg>
      );
    case "utilidades":
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M4 5h13v14a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V5Z" /><path d="M17 8h2a1 1 0 0 1 1 1v9a2 2 0 0 1-2 2M7 8h6M7 12h6M7 16h4" />
        </svg>
      );
    default:
      return null;
  }
}

const IG = () => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true"><rect x="3.5" y="3.5" width="17" height="17" rx="5" /><circle cx="12" cy="12" r="3.6" /><circle cx="17.3" cy="6.7" r="1" fill="currentColor" stroke="none" /></svg>);
const TG = () => (<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M21.9 4.3 18.6 20c-.2 1-.9 1.3-1.8.8l-4.8-3.6-2.3 2.2c-.3.3-.5.5-.9.5l.3-4.7 8.6-7.8c.4-.3-.1-.5-.6-.2L6.2 13 1.6 11.5c-1-.3-1-.9.2-1.4l18.6-7.2c.8-.3 1.6.2 1.5 1.4Z" /></svg>);
const WA = () => (<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 2a10 10 0 0 0-8.6 15l-1.3 4.7 4.8-1.3A10 10 0 1 0 12 2Zm0 18a8 8 0 0 1-4.1-1.1l-.3-.2-2.8.8.8-2.8-.2-.3A8 8 0 1 1 12 20Zm4.5-5.7c-.2-.1-1.4-.7-1.6-.8s-.4-.1-.5.1l-.7.9c-.1.2-.3.2-.5.1a6.5 6.5 0 0 1-3.2-2.8c-.2-.4.2-.4.6-1.2.1-.1 0-.3 0-.4l-.8-1.8c-.2-.5-.4-.4-.5-.4h-.5a1 1 0 0 0-.7.3 3 3 0 0 0-.9 2.2 5.2 5.2 0 0 0 1.1 2.7 11.7 11.7 0 0 0 4.5 4c2.1.8 2.1.5 2.5.5a2.6 2.6 0 0 0 1.7-1.2 2.1 2.1 0 0 0 .1-1.2c0-.1-.2-.2-.4-.3Z" /></svg>);
const YT = () => (<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M23 12s0-3.6-.5-5.3a2.7 2.7 0 0 0-1.9-1.9C18.9 4.3 12 4.3 12 4.3s-6.9 0-8.6.5A2.7 2.7 0 0 0 1.5 6.7C1 8.4 1 12 1 12s0 3.6.5 5.3a2.7 2.7 0 0 0 1.9 1.9c1.7.5 8.6.5 8.6.5s6.9 0 8.6-.5a2.7 2.7 0 0 0 1.9-1.9C23 15.6 23 12 23 12ZM9.8 15.3V8.7l5.7 3.3-5.7 3.3Z" /></svg>);

const notifKey = (n: NotificationItem) => n.id || `${n.title}|${n.text}`;

const Navbar: React.FC<NavbarProps> = ({ config = defaultSiteConfig, userName, onPrimaryAction, primaryLabel, currentPage, isLoggedIn, onUnlock, onOpenCertificates, progressPercent }) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifRead, setNotifRead] = useState<string[]>(() => {
    try { return JSON.parse(localStorage.getItem("bc:notifRead") || "[]"); } catch { return []; }
  });
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem("bc:sidebar") === "1"; } catch { return false; }
  });

  useEffect(() => {
    try { localStorage.setItem("bc:sidebar", collapsed ? "1" : "0"); } catch { /* ignore */ }
    document.body.classList.toggle("bc-collapsed", collapsed);
  }, [collapsed]);

  useEffect(() => {
    if (isLoggedIn) document.body.classList.add("has-bc-shell");
    else document.body.classList.remove("has-bc-shell");
    return () => document.body.classList.remove("has-bc-shell");
  }, [isLoggedIn]);

  useEffect(() => setDrawerOpen(false), [currentPage]);

  const firstName = (userName || "Aluno").trim().split(" ")[0] || "Aluno";
  const initial = (userName || "A").trim().charAt(0).toUpperCase();
  const links = config.links;
  const notifications = config.notifications || [];
  const visibleNotifs = notifications.filter((n) => !notifRead.includes(notifKey(n)));
  const dismissNotif = (n: NotificationItem) => {
    setNotifRead((prev) => {
      const k = notifKey(n);
      const next = prev.includes(k) ? prev : [...prev, k];
      try { localStorage.setItem("bc:notifRead", JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  };
  const community = [
    { url: links.instagram, label: "Instagram", icon: <IG /> },
    { url: links.telegram, label: "Telegram", icon: <TG /> },
    { url: links.whatsapp, label: "Comunidade", icon: <WA /> },
    { url: links.youtube, label: "YouTube", icon: <YT /> },
  ].filter((c) => c.url);

  if (!isLoggedIn) {
    return (
      <header className="bc-topbar bc-topbar-public">
        <a href="#/login" className="bc-brand">
          <span className="bc-brand-name">{config.brand.pre}<b>.</b>{config.brand.suf}</span>
        </a>
      </header>
    );
  }

  return (
    <>
      <aside className={`bc-sidebar ${drawerOpen ? "is-open" : ""} ${collapsed ? "is-collapsed" : ""}`}>
        <div className="bc-side-top">
          <a href="#/home" className="bc-brand bc-sidebar-brand" title="Blue Capital">
            <span className="bc-brand-name bc-side-txt">{config.brand.pre}<b>.</b>{config.brand.suf}</span>
          </a>
          <button
            type="button"
            className="bc-collapse-btn"
            onClick={() => setCollapsed((c) => !c)}
            aria-label={collapsed ? "Expandir menu" : "Encolher menu"}
            title={collapsed ? "Expandir menu" : "Encolher menu"}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="m14 6-6 6 6 6" /></svg>
          </button>
        </div>

        <nav className="bc-side-nav">
          <div className="bc-side-label bc-side-txt">Menu</div>
          {navLinks.map((link) => (
            <a
              key={link.key}
              href={link.href}
              className={`bc-side-link ${currentPage === link.key ? "is-active" : ""}`}
              title={collapsed ? link.label : undefined}
            >
              <span className="bc-side-ic"><NavIcon pageKey={link.key} /></span>
              <span className="bc-side-txt">{link.label}</span>
              <svg className="bc-side-arrow bc-side-txt" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="m9 6 6 6-6 6" /></svg>
            </a>
          ))}
        </nav>

        {community.length ? (
          <nav className="bc-side-nav bc-side-community">
            <div className="bc-side-label bc-side-txt">Comunidade</div>
            {community.map((c) => (
              <a key={c.label} href={c.url} target="_blank" rel="noreferrer" className="bc-side-link" title={c.label}>
                <span className="bc-side-ic bc-side-ic-social">{c.icon}</span>
                <span className="bc-side-txt">{c.label}</span>
              </a>
            ))}
          </nav>
        ) : null}

        {typeof progressPercent === "number" ? (
          <button type="button" className="bc-side-progress" onClick={onOpenCertificates} title="Ver certificados">
            <div className="bc-side-progress-top">
              <span className="bc-side-progress-label">
                <span className="bc-side-progress-ic">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="9" r="5.2" /><path d="M8.5 13.4 7 21l5-2.4L17 21l-1.5-7.6" /></svg>
                </span>
                <span className="bc-side-txt">Certificados</span>
              </span>
              <strong>{progressPercent}%</strong>
            </div>
            <div className="bc-side-progress-bar"><i style={{ width: `${Math.min(100, Math.max(0, progressPercent))}%` }} /></div>
            <span className="bc-side-progress-cta bc-side-txt">Ver certificados
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="m9 6 6 6-6 6" /></svg>
            </span>
          </button>
        ) : null}

        {links.broker ? (
          <a href={links.broker} target="_blank" rel="noreferrer" className="bc-broker" title={links.brokerName}>
            <span className="bc-broker-ic">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true"><path d="M3 21h18M5 21V10l7-5 7 5v11M9 21v-6h6v6" /></svg>
            </span>
            <span className="bc-broker-txt bc-side-txt">
              <strong>{links.brokerName}</strong>
              <small>{links.brokerDesc}</small>
            </span>
          </a>
        ) : null}

        <div className="bc-side-foot">
          <div className="bc-side-user">
            <span className="bc-side-avatar">{initial}</span>
            <span className="bc-side-user-meta bc-side-txt">
              <strong>{firstName}</strong>
              <small>Minha conta</small>
            </span>
          </div>
          <button type="button" className="bc-side-exit" onClick={onPrimaryAction} title={primaryLabel}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M15 5V4a1 1 0 0 0-1-1H5a1 1 0 0 0-1 1v16a1 1 0 0 0 1 1h9a1 1 0 0 0 1-1v-1" /><path d="M19 12H9m10 0-3-3m3 3-3 3" /></svg>
            <span className="bc-side-txt">{primaryLabel}</span>
          </button>
        </div>
      </aside>

      {/* Topbar (desktop + mobile) */}
      <header className="bc-topbar">
        <button type="button" className="bc-menu-btn" aria-label="Abrir menu" onClick={() => setDrawerOpen(true)}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M4 6h16M4 12h16M4 18h16" /></svg>
        </button>
        <a href="#/home" className="bc-brand bc-topbar-brand">
          <span className="bc-brand-name">{config.brand.pre}<b>.</b>{config.brand.suf}</span>
        </a>
        <div className="bc-topbar-title">{currentPage === "home" ? `Olá, ${firstName}` : PAGE_TITLES[currentPage] || "Área de membros"}</div>
        <div className="bc-topbar-spacer" />
        <div className="bc-notif">
          <button type="button" className="bc-notif-btn" onClick={() => setNotifOpen((o) => !o)} aria-label="Notificações">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.7 21a2 2 0 0 1-3.4 0" /></svg>
            {visibleNotifs.length ? <span className="bc-notif-dot">{visibleNotifs.length}</span> : null}
          </button>
          {notifOpen ? (
            <>
              <div className="bc-notif-backdrop" onClick={() => setNotifOpen(false)} aria-hidden="true" />
              <div className="bc-notif-pop">
                <div className="bc-notif-head">
                  <span>Notificações</span>
                  <button type="button" className="bc-notif-close" onClick={() => setNotifOpen(false)} aria-label="Fechar notificações">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18" /></svg>
                  </button>
                </div>
                {visibleNotifs.length ? (
                  visibleNotifs.map((n) => {
                    const ext = !!n.url && /^https?:\/\//i.test(n.url);
                    return (
                      <div key={notifKey(n)} className="bc-notif-item">
                        <a className="bc-notif-item-main" href={n.url || undefined} target={ext ? "_blank" : undefined} rel={ext ? "noreferrer" : undefined}>
                          <strong>{n.title}</strong>
                          <span>{n.text}</span>
                        </a>
                        <button type="button" className="bc-notif-dismiss" onClick={(e) => { e.preventDefault(); e.stopPropagation(); dismissNotif(n); }} aria-label="Marcar como lida" title="Marcar como lida">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18" /></svg>
                        </button>
                      </div>
                    );
                  })
                ) : (
                  <div className="bc-notif-empty">Você está em dia.</div>
                )}
              </div>
            </>
          ) : null}
        </div>
        <button type="button" className="bc-unlock" onClick={onUnlock}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" aria-hidden="true"><path d="m12 3 2.5 5 5.5.8-4 3.9.9 5.5L12 21l-4.9 2.6.9-5.5-4-3.9 5.5-.8L12 3Z" /></svg>
          <span>Liberar todo o acesso</span>
        </button>
        <button type="button" className="bc-topbar-exit" aria-label={primaryLabel} onClick={onPrimaryAction}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M15 5V4a1 1 0 0 0-1-1H5a1 1 0 0 0-1 1v16a1 1 0 0 0 1 1h9a1 1 0 0 0 1-1v-1" /><path d="M19 12H9m10 0-3-3m3 3-3 3" /></svg>
        </button>
      </header>

      <div className={`bc-backdrop ${drawerOpen ? "is-open" : ""}`} onClick={() => setDrawerOpen(false)} aria-hidden="true" />

      <nav className="bc-dock" aria-label="Navegação principal">
        {navLinks.map((link) => (
          <a key={link.key} href={link.href} className={`bc-dock-link ${currentPage === link.key ? "is-active" : ""}`} aria-current={currentPage === link.key ? "page" : undefined} title={link.label}>
            <NavIcon pageKey={link.key} />
            <span>{link.label}</span>
          </a>
        ))}
      </nav>
    </>
  );
};

export default Navbar;
